import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import type { ReactElement, ReactNode, SyntheticEvent } from 'react'
import type { AlertColor } from '@mui/material/Alert'
import type { SlideProps, SnackbarCloseReason } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { Alert, Button, IconButton, Slide, Snackbar, Stack } from '@mui/material'
import {
  removeSnackbarStackEntry,
  subscribeSnackbarStack,
  upsertSnackbarStackEntry
} from './snackbarStack'

const DEFAULT_AUTO_HIDE_MS = 5000
const DEFAULT_SNACKBAR_HEIGHT = 72
const SNACKBAR_GAP = 8
const SNACKBAR_BASE_BOTTOM = 24

function SlideTransition(props: SlideProps): ReactElement {
  return <Slide {...props} direction="up" />
}

interface TimedAlertSnackbarAction {
  label: string
  onClick: () => void
}

interface TimedAlertSnackbarProps {
  severity?: AlertColor
  children: ReactNode
  onClose?: () => void
  /** A button in the alert, e.g. «Открыть корзину»; pressing it also closes the snackbar. */
  action?: TimedAlertSnackbarAction
}

export default function TimedAlertSnackbar({
  severity = 'info',
  children,
  onClose,
  action
}: TimedAlertSnackbarProps): ReactElement {
  const snackbarId = useId()
  const alertRef = useRef<HTMLDivElement | null>(null)
  const [open, setOpen] = useState(true)
  const [stackEntries, setStackEntries] = useState<Array<{ id: string; height: number }>>([])

  useEffect(() => {
    setOpen(true)
  }, [children, severity])

  useEffect(() => {
    return subscribeSnackbarStack(setStackEntries)
  }, [])

  function syncStackHeight(): void {
    const measuredHeight = alertRef.current?.getBoundingClientRect().height ?? DEFAULT_SNACKBAR_HEIGHT
    upsertSnackbarStackEntry(snackbarId, measuredHeight)
  }

  useLayoutEffect(() => {
    if (!open) {
      removeSnackbarStackEntry(snackbarId)
      return
    }

    const rafId = requestAnimationFrame(syncStackHeight)
    return () => {
      cancelAnimationFrame(rafId)
    }
  }, [open, children, severity, snackbarId])

  useEffect(() => {
    return () => {
      removeSnackbarStackEntry(snackbarId)
    }
  }, [snackbarId])

  const autoHideDuration = severity === 'error' ? null : DEFAULT_AUTO_HIDE_MS
  const currentSnackbarIndex = stackEntries.findIndex((entry) => entry.id === snackbarId)
  const stackedBottomOffset = currentSnackbarIndex <= 0
    ? SNACKBAR_BASE_BOTTOM
    : SNACKBAR_BASE_BOTTOM + stackEntries
      .slice(0, currentSnackbarIndex)
      .reduce((sum, entry) => sum + entry.height + SNACKBAR_GAP, 0)

  function handleClose(_: Event | SyntheticEvent<unknown>, reason?: SnackbarCloseReason): void {
    if (reason === 'clickaway') return
    setOpen(false)
    removeSnackbarStackEntry(snackbarId)
    onClose?.()
  }

  const closeButton = severity === 'error' ? (
    <IconButton
      aria-label="close"
      color="inherit"
      size="small"
      onClick={(event) => handleClose(event)}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  ) : null
  const actionButton = action ? (
    <Button
      color="inherit"
      size="small"
      onClick={(event) => {
        action.onClick()
        handleClose(event)
      }}
      sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
    >
      {action.label}
    </Button>
  ) : null

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={handleClose}
      TransitionComponent={SlideTransition}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      sx={{ bottom: `${stackedBottomOffset}px` }}
    >
      <Alert
        ref={alertRef}
        severity={severity}
        variant="filled"
        onClose={severity === 'error' ? handleClose : undefined}
        action={actionButton && closeButton ? (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {actionButton}
            {closeButton}
          </Stack>
        ) : (actionButton ?? closeButton ?? undefined)}
        sx={{ width: '100%' }}
      >
        {children}
      </Alert>
    </Snackbar>
  )
}
